package org.example.project.components

import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import org.example.project.FirebaseManager
import org.example.project.UserCheckResult
import org.example.project.OrgReclaimResult
import org.example.project.UserRoleType
import org.example.project.PrimaryColor

@Composable
fun DeductLicenseUnitsSection() {
    val scope = rememberCoroutineScope()
    var deductEmail by remember { mutableStateOf("") }
    var deductAmount by remember { mutableStateOf("") }
    var isDeducting by remember { mutableStateOf(false) }
    var deductMessage by remember { mutableStateOf<String?>(null) }
    var showDeductConfirm by remember { mutableStateOf(false) }
    var deductTargetUidTail by remember { mutableStateOf("") }
    var deductTargetRole by remember { mutableStateOf(UserRoleType.USER) }

    Card(
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(modifier = Modifier.padding(24.dp).fillMaxWidth()) {
            Text(
                text = "Revoke Licenses",
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = Color(0xFF64748B) // Slate 500
            )
            Spacer(modifier = Modifier.height(16.dp))
            
            OutlinedTextField(
                value = deductEmail,
                onValueChange = { deductEmail = it },
                label = { Text("Account Email") },
                placeholder = { Text("user@example.com") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = PrimaryColor,
                    unfocusedBorderColor = Color(0xFFE2E8F0)
                )
            )
            
            Spacer(modifier = Modifier.height(12.dp))

            OutlinedTextField(
                value = deductAmount,
                onValueChange = { deductAmount = it.filter { char -> char.isDigit() } },
                label = { Text("Volume (Licenses)") },
                placeholder = { Text("Enter quantity") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = PrimaryColor,
                    unfocusedBorderColor = Color(0xFFE2E8F0)
                )
            )
            
            Spacer(modifier = Modifier.height(16.dp))
            
            Button(
                onClick = {
                    val amount = deductAmount.toIntOrNull() ?: 0
                    if (deductEmail.isNotBlank() && amount > 0) {
                        scope.launch {
                            isDeducting = true
                            deductMessage = null
                            val checkResult = FirebaseManager.userCheck(deductEmail)
                            when (checkResult) {
                                is UserCheckResult.Success -> {
                                    deductTargetUidTail = checkResult.data.uid_tail
                                    deductTargetRole = checkResult.data.role
                                    showDeductConfirm = true
                                }
                                is UserCheckResult.Error -> {
                                    deductMessage = "Error: ${checkResult.message}"
                                }
                            }
                            isDeducting = false
                        }
                    }
                },
                enabled = !isDeducting && deductEmail.isNotBlank() && (deductAmount.toIntOrNull() ?: 0) > 0,
                modifier = Modifier.fillMaxWidth(),
                colors = ButtonDefaults.buttonColors(containerColor = PrimaryColor),
                shape = MaterialTheme.shapes.medium
            ) {
                if (isDeducting) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(20.dp),
                        color = Color.White,
                        strokeWidth = 2.dp
                    )
                } else {
                    Text("Revoke", fontWeight = FontWeight.Bold)
                }
            }

            if (deductMessage != null) {
                val isError = deductMessage!!.startsWith("Error")
                Spacer(modifier = Modifier.height(16.dp))
                Surface(
                    color = if (isError) Color(0xFFFEF2F2) else Color(0xFFF0FDF4),
                    shape = MaterialTheme.shapes.small,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(
                        modifier = Modifier.padding(16.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = deductMessage!!,
                            color = if (isError) Color(0xFFDC2626) else Color(0xFF16A34A),
                            style = MaterialTheme.typography.bodySmall,
                            fontWeight = FontWeight.Medium
                        )
                    }
                }
            }
        }
    }

    if (showDeductConfirm) {
        val canDeduct = deductTargetRole != UserRoleType.ADMIN
        AlertDialog(
            onDismissRequest = { 
                if (!isDeducting) showDeductConfirm = false 
            },
            title = { Text(if (canDeduct) "Confirm Revocation" else "Cannot Revoke", fontWeight = FontWeight.Bold) },
            text = { 
                Column {
                    Text("Target Account: $deductEmail", color = Color(0xFF334155), fontWeight = FontWeight.Medium)
                    Text("UID ends with: $deductTargetUidTail", style = MaterialTheme.typography.bodySmall, color = Color(0xFF64748B))
                    Spacer(modifier = Modifier.height(8.dp))
                    if (canDeduct) {
                        Text("Are you sure you want to REVOKE ${deductAmount.toIntOrNull() ?: 0} licenses from this user?", color = Color(0xFFDC2626), fontWeight = FontWeight.Medium)
                    } else {
                        Text("You cannot revoke licenses from another Admin.", color = Color(0xFFDC2626), fontWeight = FontWeight.Medium)
                    }
                }
            },
            confirmButton = {
                val amount = deductAmount.toIntOrNull() ?: 0
                Button(
                    onClick = {
                        if (amount > 0 && canDeduct) {
                            scope.launch {
                                isDeducting = true
                                val result = FirebaseManager.orgReclaim(deductEmail, amount)
                                when (result) {
                                    is OrgReclaimResult.Success -> {
                                        deductMessage = "Successfully revoked $amount licenses from $deductEmail!"
                                        deductEmail = ""
                                        deductAmount = ""
                                        showDeductConfirm = false
                                    }
                                    is OrgReclaimResult.Error -> {
                                        deductMessage = "Error: ${result.message}"
                                        showDeductConfirm = false
                                    }
                                }
                                isDeducting = false
                            }
                        }
                    },
                    enabled = !isDeducting && canDeduct,
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFDC2626)) // Red 600 for danger action
                ) {
                    if (isDeducting) {
                        CircularProgressIndicator(modifier = Modifier.size(16.dp), color = Color.White, strokeWidth = 2.dp)
                    } else {
                        Text("Confirm Revocation", fontWeight = FontWeight.Bold)
                    }
                }
            },
            dismissButton = {
                TextButton(onClick = { if (!isDeducting) showDeductConfirm = false }) {
                    Text("Cancel", color = Color(0xFF64748B))
                }
            }
        )
    }
}
